import { useState, useEffect, useRef } from 'react';
import styles from './DocToolsBar.module.css';

interface Attachment {
  name: string;
  file: string;
  desc: string;
  url: string;
}

interface MenuItem {
  id: string;
  icon: string;
  label: string;
  items?: Array<{ name: string; desc: string; url?: string; onClick?: () => void }>;
}

interface Props {
  attachments: Attachment[];
  /** 文档完整 slug（category/doc-slug），用于构造 .md 原文直链 */
  docSlug: string;
}

export default function DocToolsBar({ attachments, docSlug }: Props) {
  const [openId, setOpenId] = useState<string | null>(null);
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (barRef.current && !barRef.current.contains(e.target as Node)) {
        setOpenId(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const menuItems: MenuItem[] = [
    {
      id: 'assets',
      icon: '📎',
      label: '资源',
      items: attachments.length
        ? attachments.map((a) => ({
            name: a.name,
            desc: a.desc,
            url: a.url,
          }))
        : undefined,
    },
    {
      id: 'markdown',
      icon: '📄',
      label: '查看原文',
      // .md 直链：Nginx 重写 /{slug}.md → /api/docs/{slug}/raw，读取 DB 正文，
      // 每篇文档必有，不依赖额外上传的附件
      items: [{ name: 'Markdown 原文', desc: `/${docSlug}.md`, url: `/${docSlug}.md` }],
    },
  ];

  const visibleItems = menuItems.filter((item) => {
    if (item.id === 'assets') return attachments.length > 0;
    return true;
  });

  return (
    <div className={styles.bar} ref={barRef}>
      <div className={styles.buttons}>
        {visibleItems.map((item) => (
          <button
            key={item.id}
            className={`${styles.toggle} ${openId === item.id ? styles.toggleActive : ''}`}
            onClick={() => setOpenId(openId === item.id ? null : item.id)}
            title={item.label}
          >
            <span className={styles.icon}>{item.icon}</span>
          </button>
        ))}
      </div>

      {visibleItems.map((item) => (
        <div
          key={item.id}
          className={`${styles.menu} ${openId === item.id ? styles.menuOpen : ''}`}
        >
          <div className={styles.menuTitle}>{item.icon} {item.label}</div>
          {item.items?.map((sub, i) =>
            sub.url ? (
              <a
                key={i}
                href={sub.url}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.menuItem}
              >
                <span className={styles.menuLink}>{sub.name}</span>
                {sub.desc && <span className={styles.menuDesc}>{sub.desc}</span>}
              </a>
            ) : (
              <div key={i} className={styles.menuItem}>
                <span className={styles.menuLink}>{sub.name}</span>
                {sub.desc && <span className={styles.menuDesc}>{sub.desc}</span>}
              </div>
            )
          )}
        </div>
      ))}
    </div>
  );
}
